import React, { useRef } from "react";
import Lottie, { type LottieComponentProps } from "lottie-react";
import { AspectRatio, type AspectRatioProps } from "@chakra-ui/react";
import { AnimatePresence, useInView } from "framer-motion";
import { ChakraBox } from "./chakra-framer";

type LottieAnimationProps = {
  /**
   * The aspect ratio for the animation, which will be used to properly reserve space
   * in the DOM before the animation has loaded.
   *
   * If you need to find the correct ratio, the width and height should be available
   * in the animation JSON file under `w` and `h` top-level properties.
   */
  ratio: number;
} & Pick<LottieComponentProps, "animationData" | "rendererSettings"> &
  Omit<AspectRatioProps, "ratio">;

/**
 * Lottie animation component.
 * Only renders the animation when the component is in view to help with performance.
 */
export function LottieAnimation({ ratio, rendererSettings, animationData, ...aspectRatioProps }: LottieAnimationProps) {
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { once: true, amount: "some" });

  const mergedSettings: LottieComponentProps["rendererSettings"] = {
    progressiveLoad: true,
    // Required to support box shadows: https://github.com/airbnb/lottie-web/wiki/Renderer-Settings#filtersize-svg-renderer
    filterSize: {
      width: "200%",
      height: "200%",
      x: "-50%",
      y: "-50%",
    },
    ...rendererSettings,
  };
  return (
    <AspectRatio ref={scope} ratio={ratio} {...aspectRatioProps}>
      <AnimatePresence>
        {isInView && (
          <ChakraBox initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Lottie rendererSettings={mergedSettings} animationData={animationData} />
          </ChakraBox>
        )}
      </AnimatePresence>
    </AspectRatio>
  );
}
